import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DataTable } from '../../components/DataTable/DataTable';
import { useDataTable } from '../../components/DataTable/useDataTable';
import { SatFacturasFaltantesService } from '../../services/SatFacturasFaltantesService';
import moment from 'moment';
import { Button, Icon, Chip } from '@material-ui/core';

const TIPO_DOC_CONFIG = {
    'Factura': { label: 'Factura', color: '#1976d2', bg: '#e3f2fd' },
    'Nota de Crédito': { label: 'Nota de Crédito', color: '#e65100', bg: '#fff3e0' },
    'Pago': { label: 'Pago', color: '#2e7d32', bg: '#e8f5e9' },
};

const QUICK_FILTERS = [
    { label: 'Todos', value: '' },
    { label: 'Facturas', value: 'Factura' },
    { label: 'Notas de Crédito', value: 'Nota de Crédito' },
    { label: 'Pagos', value: 'Pago' },
];

const FacturasSatFaltantes = () => {
    const tableState = useDataTable({ 
        initialLimit: 15, 
        initialOrderBy: 'fecha_emision', 
        initialOrder: 'desc' 
    });

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const requestIdRef = useRef(0);

    const fetchData = useCallback(async () => {
        const currentRequestId = ++requestIdRef.current;
        setLoading(true);
        const rows = await SatFacturasFaltantesService.obtenerLista(
            tableState.page,
            tableState.limit,
            tableState.filters,
            tableState.orderBy,
            tableState.order
        );
        // Ignorar respuestas de peticiones anteriores si el usuario cambió de filtro rápidamente
        if (currentRequestId !== requestIdRef.current) return;
        setData(rows);
        setLoading(false);
    }, [tableState.page, tableState.limit, tableState.filters, tableState.orderBy, tableState.order]);
    
    const handleDownloadCSV = async () => {
        setLoading(true);
        const allData = await SatFacturasFaltantesService.exportCSV();
        if (allData && allData.length > 0) {
            const headers = columns.map(c => c.label).join(',');
            const rows = allData.map(row => {
                return columns.map(c => {
                    let val = row[c.id];
                    if (c.id === 'fecha_emision') val = moment(val).format('YYYY-MM-DD HH:mm');
                    if (val === null || val === undefined) val = '';
                    return `"${val.toString().replace(/"/g, '""')}"`;
                }).join(',');
            }).join('\n');
            
            const csvContent = "\uFEFF" + headers + "\n" + rows;
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.setAttribute("href", url);
            link.setAttribute("download", `Documentos_SAT_Faltantes_${moment().format('YYYY-MM-DD')}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
        setLoading(false);
    };

    const handleSelectQuickFilter = (tipoValue) => {
        const nextFilters = { ...tableState.filters };
        if (tipoValue) {
            nextFilters.tipo_documento = tipoValue;
        } else {
            delete nextFilters.tipo_documento;
        }
        tableState.handleFilterChange(nextFilters);
    };

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const hasMore = data.length === tableState.limit;
    const currentTipoFilter = tableState.filters?.tipo_documento || '';

    const columns = [
        {
            id: 'tipo_documento',
            label: 'Tipo',
            filter: true,
            render: (row) => {
                const tipo = row.tipo_documento || (row.efecto_comprobante === 'I' ? 'Factura' : row.efecto_comprobante === 'E' ? 'Nota de Crédito' : row.efecto_comprobante === 'P' ? 'Pago' : row.efecto_comprobante);
                const conf = TIPO_DOC_CONFIG[tipo] || { label: tipo, color: '#424242', bg: '#f5f5f5' };
                return (
                    <Chip
                        size="small"
                        label={conf.label}
                        style={{
                            backgroundColor: conf.bg,
                            color: conf.color,
                            fontWeight: 600,
                            border: `1px solid ${conf.color}40`,
                        }}
                    />
                );
            }
        },
        { id: 'uuid', label: 'UUID / Folio Fiscal', filter: true },
        { id: 'rfc_emisor', label: 'RFC Emisor', filter: true },
        { id: 'nombre_emisor', label: 'Emisor', filter: true },
        { id: 'rfc_receptor', label: 'RFC Receptor', filter: true },
        { id: 'nombre_receptor', label: 'Receptor', filter: true },
        {
            id: 'fecha_emision',
            label: 'Fecha Emisión',
            filter: true,
            render: (row) => moment(row.fecha_emision).format('DD/MM/YYYY HH:mm')
        },
        {
            id: 'total',
            label: 'Total',
            filter: true,
            render: (row) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(row.total)
        },
        { id: 'estatus', label: 'Estatus', filter: true },
    ];

    return (
        <div className="w-full p-4 md:p-6">
            <div className="mb-4 flex flex-col md:flex-row md:justify-between md:items-center gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Documentos SAT Faltantes</h1>
                    <p className="text-gray-600">Comprobantes (Facturas, Notas de Crédito, Pagos) registrados en el SAT que no se encuentran en SOLEXX.</p>
                </div>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Icon>download</Icon>}
                    onClick={handleDownloadCSV}
                    disabled={loading}
                >
                    Descargar CSV
                </Button>
            </div>

            {/* Quick Filters */}
            <div className="mb-4 flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-gray-600 mr-1">Filtrar por tipo:</span>
                {QUICK_FILTERS.map((qf) => {
                    const isSelected = currentTipoFilter === qf.value;
                    return (
                        <button
                            key={qf.value}
                            type="button"
                            onClick={() => handleSelectQuickFilter(qf.value)}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer border ${
                                isSelected
                                    ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                            }`}
                        >
                            {qf.label}
                        </button>
                    );
                })}
            </div>

            <DataTable
                columns={columns}
                data={data}
                loading={loading}
                hasMore={hasMore}
                page={tableState.page}
                limit={tableState.limit}
                onPageChange={tableState.handlePageChange}
                onLimitChange={tableState.handleLimitChange}
                orderBy={tableState.orderBy}
                order={tableState.order}
                onSort={tableState.handleSort}
                filters={tableState.filters}
                onFilterChange={tableState.handleFilterChange}
            />
        </div>
    );
};

export default FacturasSatFaltantes;
